import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { addPA, searchPA, getByIdPA } from '../../context/users/UserActions'
import { useUserContext } from '../../context/users/UserContext'
import {
  FaCalendarAlt,
  FaSave,
  FaExclamationTriangle,
  FaCheckCircle,
} from 'react-icons/fa'
import { Card, Row, Col, Form, Button, Alert } from 'react-bootstrap'
import './AddSession.css'

const SESSION_KEY = process.env.REACT_APP_SESSION_KEY || 'alleanza_jwtoken'

function pickNumericPlanId(value) {
  if (value == null || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

function extractPlanIdFromPlanPayload(planPayload) {
  if (planPayload == null || typeof planPayload === 'string') return null
  if (typeof planPayload !== 'object') return null

  let id =
    pickNumericPlanId(planPayload.planId) ??
    pickNumericPlanId(planPayload.plan_id)
  if (id != null) return id

  id =
    pickNumericPlanId(planPayload.plan?.planId) ??
    pickNumericPlanId(planPayload.plan?.plan_id) ??
    pickNumericPlanId(planPayload.data?.planId) ??
    pickNumericPlanId(planPayload.data?.plan_id)
  if (id != null) return id

  const lists = [
    planPayload.planLicenses,
    planPayload.licenses,
    planPayload.memberships,
    planPayload.data?.planLicenses,
  ]
  for (const list of lists) {
    if (!Array.isArray(list)) continue
    for (const row of list) {
      const rowId = pickNumericPlanId(row?.planId) ?? pickNumericPlanId(row?.plan_id)
      if (rowId != null) return rowId
    }
  }
  return null
}

async function resolveUserPlanId(authUser) {
  if (!authUser?.userId || !authUser?.value) return null
  let planId = pickNumericPlanId(authUser.planId)
  if (planId != null) return planId
  const planPayload = await getByIdPA({
    path: 'plan',
    id: authUser.userId,
    token: authUser.value,
  })
  return extractPlanIdFromPlanPayload(planPayload)
}

// eslint-disable-next-line
export default function (props) {
  const navigate = useNavigate()
  const { activeSessions, authenticatedUser, authenticateUser, setToken, setAlert, setActiveSessions } = useUserContext()

  const [sessionName, setSessionName] = useState('')
  const [description, setDescription] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [resolvedPlanId, setResolvedPlanId] = useState(null)
  const [planCheckDone, setPlanCheckDone] = useState(false)

  const handleChangeSessionName = (e) => {
    setSessionName(e.target.value)
    setErrors((prev) => ({
      ...prev,
      sessionName: e.target.value.trim() === '' ? 'El nombre de la sesión es requerido' : '',
    }))
  }

  const handleChangeDescription = (e) => {
    setDescription(e.target.value)
  }

  const validateForm = () => {
    const newErrors = {}

    if (!sessionName.trim()) {
      newErrors.sessionName = 'El nombre de la sesión es requerido'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleCancel = () => {
    navigate('/sessions')
  }

  const fetchData = async () => {
    const response = await searchPA({ path: 'sessions', token: authenticatedUser.value })
    if (response.error || !Array.isArray(response.data)) {
      setAlert({
        message: response.message || 'Error en la transacción. Si persiste el error, por favor contacte a soporte.',
        msgType: 'warning',
      })
    } else {
      setActiveSessions(response.activeSession)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  useEffect(() => {
    let cancelled = false
    setPlanCheckDone(false)
    ;(async () => {
      try {
        const pid = await resolveUserPlanId(authenticatedUser)
        if (!cancelled) {
          setResolvedPlanId(pid)
          setPlanCheckDone(true)
        }
      } catch {
        if (!cancelled) {
          setResolvedPlanId(null)
          setPlanCheckDone(true)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [authenticatedUser?.userId, authenticatedUser?.value, authenticatedUser?.planId])

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      setAlert({
        message: 'Por favor corrige los errores en el formulario',
        msgType: 'warning',
      })
      return
    }

    let canCreate = true
    if (activeSessions) {
      canCreate = window.confirm(
        '¿Seguro que deseas crear una nueva sesión? La sesión activa actual se cerrará, ya que solo puede haber una sesión activa a la vez.',
      )
    }

    if (canCreate) {
      setIsLoading(true)

      try {
        let planId =
          resolvedPlanId != null && Number.isFinite(resolvedPlanId) ? resolvedPlanId : null
        if (planId == null) {
          planId = await resolveUserPlanId(authenticatedUser)
        }

        if (planId == null || !Number.isFinite(planId)) {
          setAlert({
            message:
              'No hay un plan asociado a tu cuenta. Asociá un plan o revisá tus licencias antes de crear una sesión.',
            msgType: 'warning',
          })
          setIsLoading(false)
          return
        }

        const sessionData = await addPA({
          path: 'sessions',
          data: {
            sessionName: sessionName.trim(),
            description: description || '',
            planId,
            plan_id: planId,
          },
          token: authenticatedUser.value,
        })

        if (Object.prototype.hasOwnProperty.call(sessionData, 'sessionCode')) {
          const userData = {
            ...authenticatedUser,
            sessionCode: sessionData.sessionCode,
            planId,
          }
          authenticateUser(userData)
          setToken(authenticatedUser.value)
          localStorage.setItem(SESSION_KEY, JSON.stringify(userData))
          localStorage.setItem('alleanza_currentSession', JSON.stringify({ sessionCode: sessionData.sessionCode }))

          setAlert({
            message: 'Sesión creada exitosamente. Ahora puedes configurar las preguntas y fotos.',
            msgType: 'success',
          })

          navigate(`/session/${sessionData.sessionId}/setup`)
        } else {
          const rawMsg =
            typeof sessionData === 'string'
              ? sessionData
              : sessionData?.message || sessionData?.error
          const msgText = rawMsg != null ? String(rawMsg) : 'No se pudo crear la sesión.'
          const isPlanMissing = msgText.includes('plan_id') && msgText.includes('null value')
          setAlert({
            message: isPlanMissing
              ? 'Tu cuenta no tiene un plan reconocido por el servidor. Revisá tus licencias o asociá un plan y volvé a intentar.'
              : msgText,
            msgType: 'warning',
          })
        }
      } catch (error) {
        setAlert({
          message: 'Error al crear la sesión. Por favor, inténtalo de nuevo.',
          msgType: 'error',
        })
      } finally {
        setIsLoading(false)
      }
    }
  }

  const noPlanBlocking = planCheckDone && resolvedPlanId == null
  const submitDisabled = isLoading || !planCheckDone || noPlanBlocking

  return (
    <div className="add-session-container">
      <div className="container">
        <Row className="justify-content-center">
          <Col lg={8} md={10} sm={12}>
            <div className="page-header">
              <div className="header-content">
                <div className="header-icon">
                  <div className="icon-circle">
                    <FaCalendarAlt className="icon" />
                  </div>
                </div>
                <div className="header-text">
                  <h1 className="header-title">Crear Nueva Sesión</h1>
                  <p className="header-subtitle">Completa la información para crear una nueva sesión de trabajo</p>
                </div>
              </div>
            </div>

            {noPlanBlocking && (
              <Alert variant="warning" className="mb-3">
                <strong>No hay plan asociado.</strong> Para crear sesiones necesitás una membresía con plan activo.
                <div className="mt-2">
                  <Link to="/seleccionaPlan">Ir a seleccionar plan</Link>
                  {' · '}
                  <Link to="/licencias">Ver licencias</Link>
                </div>
              </Alert>
            )}

            <Card className="form-card">
              <Card.Body>
                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col lg={12} md={12} className="mb-4">
                      <Form.Group className="form-group-custom">
                        <Form.Label className="form-label">
                          <div className="label-content">
                            <FaCalendarAlt className="field-icon" />
                            <span>Nombre de la Sesión</span>
                            <span className="required-field">*</span>
                          </div>
                        </Form.Label>
                        <div className="input-container">
                          <Form.Control
                            type="text"
                            placeholder="Ingresa el nombre de la sesión"
                            value={sessionName}
                            onChange={handleChangeSessionName}
                            className={`form-control-custom ${errors.sessionName ? 'is-invalid' : ''}`}
                            disabled={isLoading}
                          />
                        </div>
                        {errors.sessionName && (
                          <div className="error-message">
                            <FaExclamationTriangle className="error-icon" />
                            {errors.sessionName}
                          </div>
                        )}
                      </Form.Group>
                    </Col>

                    <Col lg={12} md={12} className="mb-4">
                      <Form.Group className="form-group-custom">
                        <Form.Label className="form-label">
                          <div className="label-content">
                            <FaCheckCircle className="field-icon" />
                            <span>Descripción</span>
                          </div>
                        </Form.Label>
                        <div className="input-container">
                          <Form.Control
                            as="textarea"
                            rows={4}
                            placeholder="Describe el propósito o contenido de la sesión (opcional)"
                            value={description}
                            onChange={handleChangeDescription}
                            className="form-control-custom textarea-custom"
                            disabled={isLoading}
                          />
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className="form-actions">
                    <Button
                      type="button"
                      variant="outline-secondary"
                      className="cancel-button"
                      onClick={handleCancel}
                      disabled={isLoading}
                    >
                      Cancelar
                    </Button>
                    <Button type="submit" variant="primary" className="save-button" disabled={submitDisabled}>
                      {isLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                          Creando Sesión...
                        </>
                      ) : !planCheckDone ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                          Verificando plan...
                        </>
                      ) : noPlanBlocking ? (
                        <>
                          <FaExclamationTriangle className="me-2" />
                          Sin plan
                        </>
                      ) : (
                        <>
                          <FaSave className="me-2" />
                          Crear Sesión
                        </>
                      )}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}
